import { Avatar, AvatarGroup, Button, addToast } from "@heroui/react";
import React, { useState } from "react";
import VoteModalPopup from "../vote-modal-popup";
import CountDownComponent from "../../common-layout/counter-component";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useSelector } from "react-redux";
import { useWallet } from "../../../context/WalletContext";
import { formatNumber } from "../../../utils/common";
import { getUser } from "../../../redux/reducer/user_reducer";

dayjs.extend(utc);

const StatusIndicator = ({ end_at }) => {
  const endDate = dayjs.utc(end_at);
  const now = dayjs.utc();

  const differenceInHours = endDate.diff(now, "hour"); // UTC difference

  return (
    <div
      className={`flex items-center gap-1.5 w-fit h-6 py-1 px-2.5 rounded cursor-pointer ${
        differenceInHours > 12 ? "bg-[#71B243]" : "bg-[#FF6C00]"
      }`}
    >
      <div
        className={`w-2 h-2 rounded-full ${
          differenceInHours > 12 ? "bg-[#1E4800]" : "bg-white"
        }`}
      ></div>
      <p className="m-0 text-[0.625rem] font-medium text-black">
        {differenceInHours > 12 ? "Active" : "Ending Soon"}
      </p>
    </div>
  );
};

const VoteCard = ({ data, onVoted }) => {
  const user = useSelector(getUser);
  const is_custodial_wallet = user?.is_custodial_wallet;
  const [isOpenPopup, setIsOpenPopup] = useState(false);
  const [votingTimeIsCompleted, setVotingTimeIsCompleted] = useState(false);
  const [hasVoted, setHasVoted] = useState(data?.user_voted_option);

  const isLogin = useSelector((state) => state?.user?.login);
  const { isConnected, connectWallet, hasAddressMismatch } = useWallet();

  // Open vote modal
  const handleOpenModal = () => {
    if (!isLogin) {
      addToast({
        title: "Login Required",
        description: "Please log in to cast your vote.",
        color: "warning",
        variant: "bordered"
      });
      return;
    }

    if (!isConnected && !is_custodial_wallet) {
      addToast({
        title: "Connect Wallet",
        description: "Please connect your wallet to vote.",
        color: "warning",
        variant: "bordered"
      });
      setTimeout(() => connectWallet(), 1000);
      return;
    }

    if ((isConnected && !user?.address) || hasAddressMismatch) {
      addToast({
        title: !hasAddressMismatch ? "Profile Incomplete" : "Address Mismatch",
        description: !hasAddressMismatch
          ? "Wallet address belongs to another account. Please reconnect with the correct one."
          : "The connected wallet address does not match your registered address. Please reconnect with the correct one.",
        color: "warning",
        variant: "bordered"
      });
      return;
    }
    setIsOpenPopup(!isOpenPopup);
  };

  // Handle successful vote
  const handleSuccessfulVote = () => {
    setHasVoted(true);
    if (onVoted) onVoted();
  };

  return (
    <>
      <div className="border border-[#C2C2C2] rounded-xl overflow-hidden h-full">
        <div className="bg-black h-5 flex items-center justify-center px-2">
          <p className="m-0 text-xs text-white text-center">
            Voting Ends in:&nbsp;
            <span className="font-medium">
              <CountDownComponent
                date={data?.end_date}
                isSetTime={setVotingTimeIsCompleted}
                isUtc={true}
              />
            </span>
          </p>
        </div>
        <div className="p-2.5 flex flex-col gap-5 h-[calc(100%-1.25rem)]">
          <div className="flex items-center justify-between">
            <StatusIndicator end_at={data?.end_date} />
            <div className="bg-[#E9E9E9] h-6 flex items-center gap-1.5 px-2 rounded">
              {data?.total_votes > 0 && (
                <AvatarGroup>
                  {Array.from({ length: Math.min(3, data.total_votes) }).map(
                    (_, idx) => {
                      const image = data.total_votes_profile_images?.[idx];
                      return (
                        <Avatar
                          key={idx}
                          src={typeof image === "string" ? image : undefined}
                          classNames={{
                            base: "ring-0 w-5 h-5 border border-white data-[focus-visible=true]:translate-x-0 data-[hover=true]:translate-x-0"
                          }}
                        />
                      );
                    }
                  )}
                </AvatarGroup>
              )}
              <p className="m-0 text-black text-[0.5rem] font-medium">
                {formatNumber(data?.total_votes || 0)}
              </p>
            </div>
          </div>

          <h6 className="m-0 text-base font-medium text-black line-clamp-2 min-h-[3rem]">
            {data?.name}
          </h6>

          {hasVoted ? (
            <Button
              className="bg-[#CCCDC6] border border-black rounded h-7 mt-auto"
              disabled
            >
              Voted
            </Button>
          ) : (
            <Button
              className="bg-yellowbuy border border-black rounded h-7 mt-auto"
              disabled={votingTimeIsCompleted}
              onPress={handleOpenModal}
            >
              Vote Now
            </Button>
          )}
        </div>
      </div>

      {isOpenPopup && (
        <VoteModalPopup
          isOpen={isOpenPopup}
          onOpenChange={handleOpenModal}
          data={data}
          onVoted={handleSuccessfulVote}
        />
      )}
    </>
  );
};

export default VoteCard;
